import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { X, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const KIND_LABEL = { clarification: 'Clarifications', inclusion: 'Inclusions', exclusion: 'Exclusions' };

/** Pick standard clauses from the library (grouped by category) for one kind. */
export const ClauseLibraryPicker = ({ kind, quoteType, selectedLibraryIds, onAdd, onClose, canEdit }) => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [newText, setNewText] = useState('');
  const [newCat, setNewCat] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/quote-doc/library`, { params: { kind } });
      setItems(r.data);
    } catch (e) { toast.error('Could not load the clause library'); setItems([]); }
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line

  const grouped = useMemo(() => {
    const out = {};
    (items || []).filter((c) => !quoteType || (c.quote_types || []).includes(quoteType) || (c.quote_types || []).length === 0)
      .filter((c) => !q || c.text.toLowerCase().includes(q.toLowerCase()) || c.category.toLowerCase().includes(q.toLowerCase()))
      .forEach((c) => { (out[c.category] = out[c.category] || []).push(c); });
    return out;
  }, [items, q, quoteType]);

  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const addToLibrary = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/quote-doc/library`, { kind, category: newCat.trim() || 'General', text: newText.trim(), quote_types: ['millwright', 'fabrication'] });
      setItems((s) => [...(s || []), r.data]);
      setPicked((s) => new Set([...s, r.data.id]));
      setNewText(''); setNewCat('');
      toast.success('Added to the library');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add to the library'); } finally { setSaving(false); }
  };

  const confirm = () => {
    const chosen = (items || []).filter((c) => picked.has(c.id));
    onAdd(chosen);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="clause-picker">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h3 className="font-semibold text-slate-900">Standard {KIND_LABEL[kind]}</h3>
            <p className="text-xs text-slate-500">Tick the lines to add. Already-included lines are greyed out.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="clause-picker-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-2 border-b">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search wording or category…" className="h-9" data-testid="clause-picker-search" />
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
          {items === null && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          {items !== null && Object.keys(grouped).length === 0 && <p className="text-sm text-slate-500">Nothing in the library matches.</p>}
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{cat}</p>
              <ul className="space-y-1">
                {list.map((c) => {
                  const already = selectedLibraryIds.has(c.id);
                  return (
                    <li key={c.id}>
                      <label className={`flex items-start gap-2 rounded px-2 py-1.5 text-sm ${already ? 'text-slate-500' : 'hover:bg-slate-50 cursor-pointer text-slate-800'}`}>
                        <input type="checkbox" className="mt-1" disabled={already} checked={already || picked.has(c.id)} onChange={() => toggle(c.id)} data-testid={`clause-pick-${c.id}`} />
                        <span>{c.text}{c.default_on && <span className="ml-2 text-[10px] uppercase text-blue-600 font-semibold">default</span>}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="px-5 py-3 border-t bg-slate-50 space-y-2">
            <p className="text-xs font-medium text-slate-600">Add a new standard line to the library</p>
            <div className="flex gap-2">
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Category" className="h-9 w-44" data-testid="clause-new-category" />
              <Textarea value={newText} onChange={(e) => setNewText(e.target.value)} placeholder="Wording…" rows={1} className="min-h-[36px] flex-1" data-testid="clause-new-text" />
              <Button size="sm" variant="outline" onClick={addToLibrary} disabled={saving || !newText.trim()} data-testid="clause-new-save"><Plus className="h-4 w-4 mr-1" />Library</Button>
            </div>
          </div>
        )}
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={picked.size === 0} className="bg-blue-600 hover:bg-blue-700" data-testid="clause-picker-add">Add {picked.size || ''} selected</Button>
        </div>
      </div>
    </div>
  , document.body);
};

/** One editable list of clauses (clarifications / inclusions / exclusions). */
export const ClauseSection = ({ kind, items, onChange, quoteType, canEditLibrary, hint }) => {
  const [picker, setPicker] = useState(false);
  const libIds = useMemo(() => new Set(items.map((i) => i.library_id).filter(Boolean)), [items]);
  const update = (idx, text) => onChange(items.map((it, i) => (i === idx ? { ...it, text, library_id: it.text === text ? it.library_id : null } : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const addCustom = () => onChange([...items, { id: `new-${Date.now()}`, text: '', library_id: null }]);
  const addFromLibrary = (chosen) => onChange([...items, ...chosen.map((c) => ({ id: `lib-${c.id}-${Date.now()}`, text: c.text, library_id: c.id }))]);

  return (
    <div data-testid={`clauses-${kind}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="font-semibold text-slate-900">{KIND_LABEL[kind]}</h4>
          {hint && <p className="text-xs text-slate-500">{hint}</p>}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setPicker(true)} data-testid={`add-standard-${kind}`}>Standard…</Button>
          <Button size="sm" variant="outline" onClick={addCustom} data-testid={`add-custom-${kind}`}><Plus className="h-4 w-4 mr-1" />Custom</Button>
        </div>
      </div>
      {items.length === 0 && <p className="text-sm text-slate-500 italic py-2">None yet.</p>}
      <ul className="space-y-1.5">
        {items.map((it, idx) => (
          <li key={it.id} className="flex items-start gap-2" data-testid={`clause-row-${kind}-${idx}`}>
            <span className="mt-2.5 text-slate-500">•</span>
            <Textarea value={it.text} onChange={(e) => update(idx, e.target.value)} rows={1} placeholder="Type the line…" autoFocus={!it.text}
              className="min-h-[36px] py-1.5 text-sm flex-1 resize-none" data-testid={`clause-text-${kind}-${idx}`} />
            <button onClick={() => remove(idx)} className="mt-1.5 p-1 rounded text-slate-500 hover:text-red-600 hover:bg-red-50" title="Remove" data-testid={`clause-remove-${kind}-${idx}`}><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
      {picker && <ClauseLibraryPicker kind={kind} quoteType={quoteType} selectedLibraryIds={libIds} onAdd={addFromLibrary} onClose={() => setPicker(false)} canEdit={canEditLibrary} />}
    </div>
  );
};
